import angular from 'angular';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal.vue';
import { countBy } from 'lodash';
import Bowser from "bowser";
import i18n from '@/vue/languages';
import {
    getTankTypeTranslationKey,
    getTemperatureSensorTypeTranslationKey,
    idDeviceTypeTank,
    idDeviceTypeTempSensor, isTankDevice, isTemperatureSensorDevice
} from "@/utils/devices";
import DownloadVictronConnectModal from "@/vue/components/modals/DownloadVictronConnectModal";

angular.module('vrm.core').controller('OverviewSettingsController', OverviewSettingsController);

/**
 * @constructor
 * @ngInject
 */
function OverviewSettingsController(
    $log,
    $scope,
    $state,
    $stateParams,
    $q,
    ApiService,
    InstallationsService,
    UserService,
    VrmModalService,
    moment,
    locale,
    vrmConfig,
    vrmNotificationType,
    vrmNotificationsService,
    vrmUserAccessLevel,
    MQTTRealtimeService,
) {
    $log.info('OverviewSettingsController');
    locale.ready('device');

    //List of data fields which are shown
    $scope.visibleKeys = [
        'productName',
        'firmwareVersion',
        'productId',
        'loggingInterval',
        'identifier',
        'lastConnection',
        'lastPowerUpOrRestart',
        'twoWayCommunication',
        'vrm',
        'remoteSupport',
        'remoteSupportIpv6',
        'machineSerialNumber',
        'froniusDeviceType',
        'inputType',
        'inputState',
        'updateTo',
        'vncStatus',
        'instance',
        'pL',
        'pdV',
        'vmc',
        'vid',
        'evl',
        'remoteOnLan',
        'signalFrequency',
        'signalRSSI',
        'signalSNR',
        'antennaType',
        'networkName',
        'simCardIdentifier',
        'connectionInformation',
        'batteryManufacturer',
        'batteryFamily',
    ];

    $scope.users = [];
    $scope.devicesByName = {};

    UserService.getUser().then((user) => {
        if (user.accessLevel !== vrmUserAccessLevel.DEMO) {
            ApiService.installations($stateParams.id, 'users').then(function (response) {
                $scope.users = response.data.users;
            });
        }
    })

    $scope.mobileCheck = function () {
        const browser = Bowser.getParser(window.navigator.userAgent);

        return browser.parsedResult.platform.type === 'mobile' || browser.parsedResult.platform.type === 'tablet';
    };

    $scope.victronConnectUrlDomain = $scope.mobileCheck() ? "https://ve3.nl/vcrp" : "victron-connect://remote-product";

    const VICTRON_CONNECT_BLACKLIST = {
        // VictronConnect show these device types separately
        idDeviceType: [
            13, // Grid Meters
            25, // EVCS
        ],
        // VictronConnect does not work for these devices because they are either EOL or use VE.Bus
        productName: ['Lynx Ion', 'BlueSolar Charger MPPT 150/70 (VE.Can)', 'BlueSolar Charger MPPT 150/85 (VE.Can)', 'Quattro', 'Phoenix Multi', 'MultiPlus', 'Multi 15kVA', 'MultiGrid'],
    };

    function parseResponse(response, installation = null) {
        $scope.devices = response.data.records.devices;
        $scope.devicesByName = countBy($scope.devices, 'name');

        const attribs = (installation && installation.data) || {};
        const {tst, tf} = attribs;

        _.each($scope.devices, function (device) {

            // Mark if the device didn't communicate for longer than 1 day.
            device.idle = ((new Date() / 1000) - device.lastConnection) > 3600 * 24;
            if (typeof device.settings !== "undefined") {
                device.settings.map((entry => {
                    let currentValue = entry.value === null || entry.loggedTimestamp > entry.timestamp ? entry.loggedValue : entry.value;
                    entry.settingModel = entry.enumData.find((enumData) => parseInt(enumData.valueEnum) === parseInt(currentValue));
                    entry.currentValue = entry.enumData.find((enumData) => parseInt(enumData.valueEnum) === parseInt(entry.loggedValue));
                    entry.requestedValue = entry.enumData.find((enumData) => parseInt(enumData.valueEnum) === parseInt(entry.value));
                }));
            }

            const isTank = isTankDevice(device.idDeviceType);
            const isTempSensor = isTemperatureSensorDevice(device.idDeviceType);

            const instanceId = device.instance;

            // Apply translated tank type name as fallback if no custom name is set.
            if (isTank && !device.customName && tf) {
                const typeId = tf.instances[instanceId].rawValue;
                const translationKey = getTankTypeTranslationKey(typeId);

                if (translationKey) {
                    device.typeName = locale.getString(translationKey);
                }
            }

            // Apply translated temp sensor type name as fallback if no custom name is set.
            if (isTempSensor && !device.customName && tst) {
                const typeId = tst.instances[instanceId].rawValue;
                const translationKey = getTemperatureSensorTypeTranslationKey(typeId);

                if (translationKey) {
                    device.typeName = locale.getString(translationKey);
                }
            }

            device.inVictronConnect = !device.idle && !!device.productCode && !!device.machineSerialNumber && !!device.idDeviceType && !VICTRON_CONNECT_BLACKLIST.idDeviceType.includes(device.idDeviceType) && !!device.productName;
            for (let namePart of VICTRON_CONNECT_BLACKLIST.productName) {
                if (device.productName.includes(namePart)) {
                    device.inVictronConnect = false;
                }
            }
        });
    }

    $scope.settingsModels = {};

    $q.all([
        InstallationsService.getInstallation($stateParams.id),
        ApiService.installations($stateParams.id, 'system-overview'),
    ]).then((result) => {
        const installation = result[0];
        const overviewResponse = result[1];

        locale.ready('installationoverview').then(() => {
            parseResponse(overviewResponse, installation);
        });
    });


    $scope.updateSetting = function (setting) {
        if (typeof setting.settingModel.valueEnum === "undefined" || setting.settingModel.valueEnum === null) {
            return;
        }
        let data = {
            idDataAttribute: setting.idDataAttribute,
            value: setting.settingModel.valueEnum,
            instance: setting.instance
        }
        ApiService.installations($stateParams.id, 'update-setting', data, 'POST').then(function (response) {
            locale.ready('general').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('overview.setting_updated') });
            })
            parseResponse(response);
        });
    }

    $scope.serialNumbersNotNull = function (serialObject) {
        // we cannot return null from the backend as precedence is important hence need a flag for each device
        if (!serialObject) return false;
        return Object.entries(serialObject).some(([, value]) => value.serial !== null);
    }

    $scope.messages = {};

    $scope.hasAtLeastOneAdmin = function (users) {
        var adminCount = 0;
        for (var i = 0; i < users.length; i++) {
            if (users[i].accessLevel) {
                adminCount++;
            }
        }
        return adminCount > 1;
    };

    $scope.mqttEnabled = vrmConfig.enableMQTT;

    $scope.unlink = function () {
        VrmModalService.openConfirmModal($scope, 'unlink', function (confirmed) {
            if (confirmed) {
                ApiService.installations($stateParams.id, 'unlink', null, 'POST').then(function () {
                    locale.ready('general').then(function () {
                        vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('general.unlinked') });
                        //Reload installations list
                        InstallationsService.reload();
                        $state.go('base.installation-overview');
                    })
                });
            }
        });
    };

    $scope.openEvcc = function (device) {
        if (!device.evconn) return;
        ApiService.call('/installations/' + $stateParams.id + '/proxy-relay/evcc')
        .then(function (response) {
            $scope.evccUrl = 'https://' +
                response.data.proxy_relay.replace('*', $stateParams.id + '-evcs' + device.instance) +
                '/proxyauthorize?targethost=' + device.evconn + '&targetport=80' +
                '&proxytoken=' +
                response.data.token;

            // Safari for iOS fix
            setTimeout(function () {
                window.open($scope.evccUrl);
            });
        });
    }

    $scope.deleteDevice = function (device, event) {
        event.stopPropagation();
        VrmModalService.openDeleteModal($scope, 'device', device.name, function (confirmed) {
            if (confirmed) {
                ApiService.installations($stateParams.id, 'delete-device', { idDeviceType: device.idDeviceType, instance: device.instance }, 'POST').then(function (res) {
                    if (res.data && res.data.success) {
                        // Filter out the deleted device.
                        $scope.devices = $scope.devices.filter(function (deviceItem) {
                            return (deviceItem !== device);
                        });
                        $scope.devicesByName = countBy($scope.devices, 'name');
                        // Show the toaster.
                        locale.ready('general').then(function () {
                            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('overview.device_deleted') });
                        })
                    }
                });
            }
        });
    };

    $scope.deleteVebusDevice = function (vebusDevice, serialObj) {
        // extract the index of the VEBus connected device from its dbusPath
        const matchDbusPath = serialObj.serial.dbusPath.match(/\/Devices\/(\d+)\/SerialNumber/);
        if (!matchDbusPath) return;
        const deviceDbusPathIndex = matchDbusPath[1];
        const modalTitle = `${i18n.t('overview.vebus_connected_device')} (${$scope.getVebusConnectedDeviceType(deviceDbusPathIndex)})`;
        VrmModalService.openDeleteModal($scope, 'device', modalTitle, function (confirmed) {
            if (!confirmed) return;
            ApiService.installations($stateParams.id, 'delete-vebus-device', {
                instance: serialObj.serial.instance,
                deviceDbusPathIndex: deviceDbusPathIndex,
            }, 'POST'
            ).then(function (res) {
                if (!res.data || !res.data.success) return;
                // Filter out the deleted VEBus connected device(s)
                const vebusIndex = $scope.devices.findIndex(device => device === vebusDevice);
                $scope.devices[vebusIndex].machineSerialNumbers = $scope.devices[vebusIndex].machineSerialNumbers.filter(serialObjItem => serialObjItem !== serialObj);

                locale.ready('overview').then(function () {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.SUCCESS,
                        message: locale.getString('overview.device_deleted')
                    });
                })
            });
        });
    };

    $scope.getVebusConnectedDeviceType = function (deviceIndex) {
        // index / 3 => 0 are master, rest are parallel units.
        const phaseIndex = (deviceIndex % 3) + 1;
        const parallelUnitIndex = Math.floor(deviceIndex / 3);
        const phase = i18n.t('device.phaseIndex', {
            i18nFallback: `Phase ${phaseIndex}`,
            index: phaseIndex,
        });
        const parallelUnit = parallelUnitIndex === 0
            ? i18n.t('device.master')
            : i18n.t('device.parallelUnitIndex', {
                i18nFallback: `Parallel Unit ${parallelUnitIndex}`,
                index: parallelUnitIndex,
            });
        return `${phase} ${parallelUnit}`;
    };

    $scope.hasAnyDeletableVebusDevices = function (device) {
        return device.machineSerialNumbers.some(serialObj => $scope.canUserDeleteVebusDevice(device, serialObj));
    }

    $scope.canUserDeleteVebusDevice = function (device, serialObj) {
        const outdatedOrNotConnected = !serialObj.serial || !serialObj.serial.timestamp || $scope.outdatedTimestamp(serialObj.serial.timestamp);
        return $scope.installation && !device.idle && $scope.installation.view_permissions.update_settings && outdatedOrNotConnected;
    }

    $scope.getSignalStrength = function (signal) {
        return signal.split(' ')[0];
    }

    $scope.referToVictronConnect = function () {
        let installed = false;
        const onAppOpeningBlur = function (event) {
            installed = true;
        }
        window.addEventListener('blur', onAppOpeningBlur);
        const clickedAt = +new Date;
        setTimeout(function(){
            // To avoid failing on return to MobileSafari, ensure freshness!
            if (+new Date - clickedAt < 3000 && !installed) {
                VrmModalService.openVueModal(DownloadVictronConnectModal);
            }
            window.removeEventListener('blur', onAppOpeningBlur);
        }, 1000);
    }

    $scope.waitingForRebootAck = false;

    $scope.handleRebootAck = function(message, identifier) {
        if (message.payload.value === 1) {
            clearTimeout($scope.ackTimeout);

            locale.ready('overview').then(function() {
                vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString("overview.reboot_ack_received") });
            });

            MQTTRealtimeService.unsubScribeFromPath(
                {
                    identifier: identifier,
                    dbusServiceType: 'platform',
                    dbusPath: '/Device/Reboot',
                    instance: 0,
                    callbackIdentifier: 'rebootGX',
                    prefix: 'N/',
                },
            );

            $scope.waitingForRebootAck = false;
        }
    }

    $scope.rebootGX = async function(identifier, idSite) {
        const props = {
            message: i18n.t('overview.confirm_reboot_message'),
            action: i18n.t('overview.confirm_reboot_action'),
        };

        const confirmed = await VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            // This subscription is necessary to read the GX's ack, as the callback function isn't being called when writing
            MQTTRealtimeService.subscribeToPath(
                {
                    identifier: identifier,
                    dbusServiceType: 'platform',
                    dbusPath: '/Device/Reboot',
                    instance: 0,
                    callback: function(message) {
                        $scope.handleRebootAck(message, identifier);
                    },
                    callbackIdentifier: 'rebootGX',
                    prefix: 'N/',
                },
            );

            // Send the reboot request to the GX device
            MQTTRealtimeService.publishToPath(
                {
                    identifier: identifier,
                    dbusServiceType: 'platform',
                    dbusPath: '/Device/Reboot',
                    instance: 0,
                    callbackIdentifier: 'rebootGX',
                    prefix: 'W/',
                },
                { value: 1 }
            );

            $scope.waitingForRebootAck = true;

            // Start the timeout after sending the reboot message
            $scope.ackTimeout = setTimeout(function(identifier) {
                locale.ready('overview').then(function() {
                    vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: locale.getString("overview.reboot_ack_not_received") });
                });

                MQTTRealtimeService.unsubScribeFromPath(
                    {
                        identifier: identifier,
                        dbusServiceType: 'platform',
                        dbusPath: '/Device/Reboot',
                        instance: 0,
                        callbackIdentifier: 'rebootGX',
                        prefix: 'N/',
                    },
                );

                $scope.waitingForRebootAck = false;
            }, 4000, identifier);

            // Log the remote reboot
            ApiService.logRemoteControl(idSite, {
                dbusPath: '/Device/Reboot',
                dbusServiceType: 'platform',
                value: 1,
                oldValue: 0,
            });

            locale.ready('overview').then(function() {
                vrmNotificationsService.add({ type: vrmNotificationType.WARNING, message: locale.getString("overview.reboot_sent") });
            })
        }
    }

    $scope.logVictronConnectSite = function () {
        ApiService.installations($stateParams.id, 'system-overview', null, 'POST');
    }

    $scope.logVictronConnectDevice = function (device) {
        const formattedInstance = device.hasOwnProperty('instance') ? ` [${device.instance}]`: ''
        const deviceName = device.customName ? device.customName : device.name + formattedInstance
        ApiService.installations($stateParams.id, 'system-overview', {
            deviceName: deviceName
        }, 'POST');
    }
}
